import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { Folder, Download, Pencil, Trash2, Share2, Loader2 } from 'lucide-react'
import {
  filesApi,
  type FilesFolder,
  type FilesObject,
  type FilesPathItem,
} from '../../services/api'
import { dialog } from '../../services/dialog'
import { useFilesUpload } from './useFilesUpload'
import ShareDialog from './ShareDialog'
import { BACKEND_URL } from '../../services/config'
import {
  type FilesViewMode,
  type FilesSortKey,
  sortFolders,
  sortObjects,
  isImage,
  fileIcon,
} from './filesView'

interface Props {
  folderId: string
  viewMode: FilesViewMode
  sortKey: FilesSortKey
  onFolderSelect: (id: string) => void
  onFolderLoaded?: (folder: FilesFolder | null) => void
  onPathLoaded?: (path: FilesPathItem[]) => void
}

export interface FolderViewHandle {
  triggerUpload: () => void
  triggerNewFolder: () => void
  canWrite: boolean
}

interface FolderState {
  folder: FilesFolder
  subfolders: FilesFolder[]
  objects: FilesObject[]
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

/** Image thumbnail that lazily loads and falls back to the type icon on error. */
function ImageThumb({ obj }: { obj: FilesObject }) {
  const [failed, setFailed] = useState(false)
  const Icon = fileIcon(obj)
  if (failed) {
    return <Icon size={40} className="text-muted-foreground" strokeWidth={1.5} />
  }
  return (
    <img
      src={`${BACKEND_URL}${filesApi.blobUrl(obj.id, true)}`}
      alt={obj.name}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className="w-full h-full object-cover"
    />
  )
}

const FolderView = forwardRef<FolderViewHandle, Props>(function FolderView(
  { folderId, viewMode, sortKey, onFolderSelect, onFolderLoaded, onPathLoaded },
  ref
) {
  const [state, setState] = useState<FolderState | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shareTarget, setShareTarget] = useState<{
    kind: 'folder' | 'object'
    id: string
    name: string
  } | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { uploads, upload, clearDone } = useFilesUpload()

  // Kept as a ref so useImperativeHandle can reference it
  // without capturing a stale closure on mount
  const stateRef = useRef(state)
  stateRef.current = state

  const loadFolder = useCallback(
    async (id: string) => {
      setLoading(true)
      setError(null)
      try {
        const { data } = await filesApi.folder(id)
        setState({ folder: data.folder, subfolders: data.subfolders, objects: data.objects })
        onFolderLoaded?.(data.folder)
        onPathLoaded?.(data.path ?? [])
      } catch {
        setError('Failed to load folder. Please try again.')
        onFolderLoaded?.(null)
        onPathLoaded?.([])
      } finally {
        setLoading(false)
      }
    },
    [onFolderLoaded, onPathLoaded]
  )

  useEffect(() => {
    if (folderId) loadFolder(folderId)
  }, [folderId, loadFolder])

  const canWrite = state?.folder.write ?? false

  // Apply the selected sort — folders and files are sorted within their own
  // group so folders always render before files.
  const sortedFolders = useMemo(
    () => (state ? sortFolders(state.subfolders, sortKey) : []),
    [state, sortKey]
  )
  const sortedObjects = useMemo(
    () => (state ? sortObjects(state.objects, sortKey) : []),
    [state, sortKey]
  )

  // --- Actions ---

  const handleNewFolder = useCallback(async () => {
    const name = await dialog.prompt({ title: 'New folder', placeholder: 'Folder name', required: true })
    if (!name) return
    try {
      const { data: folderRes } = await filesApi.createFolder(folderId, name)
      setState((prev) => (prev ? { ...prev, subfolders: [...prev.subfolders, folderRes.folder] } : prev))
    } catch {
      await dialog.alert({ title: 'Failed to create folder', tone: 'destructive' })
    }
  }, [folderId])

  // Imperative handle for the brow toolbar
  useImperativeHandle(
    ref,
    () => ({
      triggerUpload: () => fileInputRef.current?.click(),
      triggerNewFolder: handleNewFolder,
      get canWrite() {
        return stateRef.current?.folder.write ?? false
      },
    }),
    [handleNewFolder]
  )

  const handleUploadFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return
      Array.from(files).forEach((f) => {
        upload(f, folderId)
          .then(() => loadFolder(folderId))
          .catch(() => {})
      })
    },
    [folderId, upload, loadFolder]
  )

  const handleRenameFolder = async (folder: FilesFolder) => {
    const name = await dialog.prompt({
      title: 'Rename folder',
      defaultValue: folder.name,
      required: true,
    })
    if (!name || name === folder.name) return
    try {
      await filesApi.rename('folders', folder.id, name)
      setState((prev) =>
        prev
          ? { ...prev, subfolders: prev.subfolders.map((f) => (f.id === folder.id ? { ...f, name } : f)) }
          : prev
      )
    } catch {
      await dialog.alert({ title: 'Failed to rename folder', tone: 'destructive' })
    }
  }

  const handleDeleteFolder = async (folder: FilesFolder) => {
    const ok = await dialog.confirm({
      title: `Delete "${folder.name}"?`,
      message: 'This folder and all its contents will be deleted.',
      tone: 'destructive',
      confirmLabel: 'Delete',
    })
    if (!ok) return
    try {
      await filesApi.remove('folders', folder.id)
      setState((prev) =>
        prev ? { ...prev, subfolders: prev.subfolders.filter((f) => f.id !== folder.id) } : prev
      )
    } catch {
      await dialog.alert({ title: 'Failed to delete folder', tone: 'destructive' })
    }
  }

  const handleRenameObject = async (obj: FilesObject) => {
    const name = await dialog.prompt({
      title: 'Rename file',
      defaultValue: obj.name,
      required: true,
    })
    if (!name || name === obj.name) return
    try {
      await filesApi.rename('objects', obj.id, name)
      setState((prev) =>
        prev
          ? { ...prev, objects: prev.objects.map((o) => (o.id === obj.id ? { ...o, name } : o)) }
          : prev
      )
    } catch {
      await dialog.alert({ title: 'Failed to rename file', tone: 'destructive' })
    }
  }

  const handleDeleteObject = async (obj: FilesObject) => {
    const ok = await dialog.confirm({
      title: `Delete "${obj.name}"?`,
      message: 'This file will be permanently deleted.',
      tone: 'destructive',
      confirmLabel: 'Delete',
    })
    if (!ok) return
    try {
      await filesApi.remove('objects', obj.id)
      setState((prev) =>
        prev ? { ...prev, objects: prev.objects.filter((o) => o.id !== obj.id) } : prev
      )
    } catch {
      await dialog.alert({ title: 'Failed to delete file', tone: 'destructive' })
    }
  }

  // --- Drag & drop ---

  const onDragOver = (e: React.DragEvent) => {
    if (!canWrite) return
    e.preventDefault()
    setDragOver(true)
  }

  const onDragLeave = () => setDragOver(false)

  const onDrop = (e: React.DragEvent) => {
    if (!canWrite) return
    e.preventDefault()
    setDragOver(false)
    handleUploadFiles(e.dataTransfer.files)
  }

  // --- Rendering ---

  if (loading && !state) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3">
        <p className="text-sm text-destructive">{error}</p>
        <button onClick={() => loadFolder(folderId)} className="text-sm text-primary hover:underline">
          Retry
        </button>
      </div>
    )
  }

  if (!state) return null

  const isEmpty = state.subfolders.length === 0 && state.objects.length === 0
  const inlineUrl = (obj: FilesObject) => `${BACKEND_URL}${filesApi.blobUrl(obj.id, true)}`

  return (
    <div
      className={`flex-1 min-h-0 overflow-y-auto relative ${dragOver ? 'ring-2 ring-inset ring-primary' : ''}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {dragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/10 pointer-events-none">
          <p className="text-primary font-medium text-lg">Drop files to upload</p>
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* Upload progress */}
        {uploads.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">Uploads</p>
              <button onClick={clearDone} className="text-xs text-muted-foreground hover:text-foreground">
                Clear done
              </button>
            </div>
            {uploads.map((u) => (
              <div key={u.id} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground truncate max-w-xs">{u.name}</span>
                  <span
                    className={`ml-2 shrink-0 ${
                      u.status === 'done'
                        ? 'text-primary'
                        : u.status === 'error'
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {u.status === 'done' ? 'Done' : u.status === 'error' ? 'Error' : `${u.pct}%`}
                  </span>
                </div>
                {u.status === 'uploading' && (
                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${u.pct}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ---------------- LIST VIEW ---------------- */}
        {viewMode === 'list' && (
          <>
            {sortedFolders.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Folders
                </p>
                <div className="space-y-0.5">
                  {sortedFolders.map((folder) => (
                    <div
                      key={folder.id}
                      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted group"
                    >
                      <button
                        onClick={() => onFolderSelect(folder.id)}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left"
                      >
                        <Folder size={16} className="text-primary shrink-0" />
                        <span className="text-sm text-foreground truncate">{folder.name}</span>
                      </button>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                        <button
                          onClick={() => handleRenameFolder(folder)}
                          title="Rename"
                          className="p-1 rounded text-muted-foreground hover:text-foreground transition"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setShareTarget({ kind: 'folder', id: folder.id, name: folder.name })}
                          title="Share"
                          className="p-1 rounded text-muted-foreground hover:text-foreground transition"
                        >
                          <Share2 size={14} />
                        </button>
                        {canWrite && (
                          <button
                            onClick={() => handleDeleteFolder(folder)}
                            title="Delete"
                            className="p-1 rounded text-muted-foreground hover:text-destructive transition"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sortedObjects.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Files
                </p>
                <div className="space-y-0.5">
                  {sortedObjects.map((obj) => {
                    const Icon = fileIcon(obj)
                    return (
                      <div
                        key={obj.id}
                        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted group"
                      >
                        <a
                          href={inlineUrl(obj)}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-3 flex-1 min-w-0"
                        >
                          <Icon size={16} className="text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground truncate">{obj.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {humanSize(obj.size)} · {new Date(obj.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </a>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                          <a
                            href={`${BACKEND_URL}${filesApi.blobUrl(obj.id)}`}
                            target="_blank"
                            rel="noreferrer"
                            title="Download"
                            className="p-1 rounded text-muted-foreground hover:text-foreground transition"
                          >
                            <Download size={14} />
                          </a>
                          <button
                            onClick={() => handleRenameObject(obj)}
                            title="Rename"
                            className="p-1 rounded text-muted-foreground hover:text-foreground transition"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => setShareTarget({ kind: 'object', id: obj.id, name: obj.name })}
                            title="Share"
                            className="p-1 rounded text-muted-foreground hover:text-foreground transition"
                          >
                            <Share2 size={14} />
                          </button>
                          {canWrite && (
                            <button
                              onClick={() => handleDeleteObject(obj)}
                              title="Delete"
                              className="p-1 rounded text-muted-foreground hover:text-destructive transition"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* ---------------- GRID VIEW ---------------- */}
        {viewMode === 'grid' && (
          <>
            {sortedFolders.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Folders
                </p>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
                  {sortedFolders.map((folder) => (
                    <div
                      key={folder.id}
                      className="group relative rounded-lg border border-border bg-card hover:bg-muted transition overflow-hidden"
                    >
                      <button
                        onClick={() => onFolderSelect(folder.id)}
                        title={folder.name}
                        className="w-full flex flex-col items-center gap-2 p-4 text-center"
                      >
                        <Folder size={44} className="text-primary" strokeWidth={1.5} />
                        <span className="text-sm text-foreground truncate w-full">{folder.name}</span>
                      </button>
                      <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 rounded-md bg-card/90 border border-border p-0.5 opacity-0 group-hover:opacity-100 transition">
                        <button
                          onClick={() => handleRenameFolder(folder)}
                          title="Rename"
                          className="p-1 rounded text-muted-foreground hover:text-foreground transition"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => setShareTarget({ kind: 'folder', id: folder.id, name: folder.name })}
                          title="Share"
                          className="p-1 rounded text-muted-foreground hover:text-foreground transition"
                        >
                          <Share2 size={13} />
                        </button>
                        {canWrite && (
                          <button
                            onClick={() => handleDeleteFolder(folder)}
                            title="Delete"
                            className="p-1 rounded text-muted-foreground hover:text-destructive transition"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sortedObjects.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Files
                </p>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
                  {sortedObjects.map((obj) => (
                    <div
                      key={obj.id}
                      className="group relative rounded-lg border border-border bg-card overflow-hidden hover:border-primary/40 transition"
                    >
                      <a
                        href={inlineUrl(obj)}
                        target="_blank"
                        rel="noreferrer"
                        title={obj.name}
                        className="block"
                      >
                        <div className="aspect-square flex items-center justify-center bg-muted overflow-hidden">
                          {isImage(obj) ? (
                            <ImageThumb obj={obj} />
                          ) : (
                            (() => {
                              const Icon = fileIcon(obj)
                              return <Icon size={40} className="text-muted-foreground" strokeWidth={1.5} />
                            })()
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-xs text-foreground truncate" title={obj.name}>
                            {obj.name}
                          </p>
                          <p className="text-[11px] text-muted-foreground">{humanSize(obj.size)}</p>
                        </div>
                      </a>
                      <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 rounded-md bg-card/90 border border-border p-0.5 opacity-0 group-hover:opacity-100 transition">
                        <a
                          href={`${BACKEND_URL}${filesApi.blobUrl(obj.id)}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Download"
                          className="p-1 rounded text-muted-foreground hover:text-foreground transition"
                        >
                          <Download size={13} />
                        </a>
                        <button
                          onClick={() => handleRenameObject(obj)}
                          title="Rename"
                          className="p-1 rounded text-muted-foreground hover:text-foreground transition"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => setShareTarget({ kind: 'object', id: obj.id, name: obj.name })}
                          title="Share"
                          className="p-1 rounded text-muted-foreground hover:text-foreground transition"
                        >
                          <Share2 size={13} />
                        </button>
                        {canWrite && (
                          <button
                            onClick={() => handleDeleteObject(obj)}
                            title="Delete"
                            className="p-1 rounded text-muted-foreground hover:text-destructive transition"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Empty state */}
        {isEmpty && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Folder size={40} className="text-muted-foreground" />
            <p className="text-sm text-muted-foreground">This folder is empty.</p>
            {canWrite && (
              <p className="text-xs text-muted-foreground">
                Use the buttons above to create a folder or upload files.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handleUploadFiles(e.target.files)}
      />

      {/* Share dialog */}
      {shareTarget && (
        <ShareDialog
          itemKind={shareTarget.kind}
          itemId={shareTarget.id}
          itemName={shareTarget.name}
          onClose={() => setShareTarget(null)}
        />
      )}
    </div>
  )
})

export default FolderView
